import React, { useState, useEffect } from 'react';
import { Spinner, Card, Button, Row, Col, Alert, Form } from 'react-bootstrap';
import axios from 'axios';
import { useTranslation } from 'react-i18next'; // ensure i18next hook imported

const WHATSAPP_NUMBER = "923001234567"; // Adjust your variable/import as needed

export default function PaymentGate({ onApproved }) {
  const { t } = useTranslation();

  const PLAN_DISPLAY = [
    { key: 'trial', label: t('planFreeTrial', 'Free Trial'), sites: t('planSites1', '1 Site'), duration: t('planDuration7Days', '7 Days'), price: 0 },
    { key: 'starter', label: t('planStarter', 'Starter'), sites: t('planSites4', 'Only 4 Sites'), duration: t('planDurationLifetime', 'Lifetime + Annual Fee'), price: 450000 },
    { key: 'enterprise', label: t('planEnterprise', 'Enterprise'), sites: t('planSites8', 'Only 8 Sites'), duration: t('planDurationLifetime', 'Lifetime + Annual Fee'), price: 750000 },
    { key: 'unlimited', label: t('planUnlimited', '1 Year Unlimited'), sites: t('planSitesUnlimited', 'Unlimited Sites'), duration: t('planDuration1Year', '1 Year'), price: 1000000 },
  ];

  const [step, setStep] = useState('loading'); // loading | select | confirm | uploading | pending | rejected
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState(null);
  const [whatsappClicked, setWhatsappClicked] = useState(false);
  const [rejectReason, setRejectReason] = useState(null);

  const token = localStorage.getItem('token');

  const checkStatus = async () => {
    try {
      const { data } = await axios.get('https://api.mfrbuild.com/api/v1/billing/my-status', {
        headers: { 'x-auth-token': token },
      });
      if (data.status === 'paid') {
        onApproved();
      } else if (data.status === 'pending') {
        setStep('pending');
      } else if (data.status === 'rejected') {
        setRejectReason(data.reviewNote);
        setStep('rejected');
      } else {
        setStep('select');
      }
    } catch (err) {
      setStep('select');
    }
  };

  useEffect(() => {
    checkStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (step !== 'pending') return;
    const interval = setInterval(checkStatus, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handlePlanClick = (planKey) => {
    if (planKey === 'trial') {
      onApproved(); // Free trial - seedha unlock, koi payment verify nahi karni
      return;
    }
    setSelectedPlan(planKey);
    setStep('confirm');
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      setMessage({ type: 'danger', text: t('errSelectReceipt', 'Pehle receipt select karein.') });
      return;
    }
    setStep('uploading');
    setMessage(null);

    try {
      const formData = new FormData();
      formData.append('plan', selectedPlan);
      formData.append('receipt', file);

      await axios.post('https://api.mfrbuild.com/api/v1/billing/submit-receipt', formData, {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'multipart/form-data',
        },
      });

      setStep('pending');
    } catch (err) {
      setMessage({ type: 'danger', text: err.response?.data?.message || t('errGeneric', 'Kuch masla ho gaya.') });
      setStep('confirm');
    }
  };

  const planInfo = PLAN_DISPLAY.find((p) => p.key === selectedPlan);
  const waMessage = planInfo
    ? t('waMessageText', 'Assalam o Alaikum, maine {{plan}} plan (Rs {{price}}) ka payment kiya hai. Receipt attach hai.', {
        plan: planInfo.label,
        price: planInfo.price.toLocaleString(),
      })
    : '';

  if (step === 'loading') {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (step === 'pending') {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <Card className="text-center shadow-sm" style={{ maxWidth: 500 }}>
          <Card.Body className="p-4">
            <h4>✅ {t('pendingReceiptTitle', 'Receipt mil gayi hai')}</h4>
            <p className="text-muted">
              {t('pendingReceiptDesc', 'Hum aapki payment verify kar rahe hain. Verification hote hi aapka account automatically activate ho jayega (aam tor pe 1-2 ghante mein).')}
            </p>
            <p className="text-muted small">{t('pendingAutoUpdateNote', 'Ye page automatically update ho jayega, ruknay ki zaroorat nahi.')}</p>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (step === 'rejected') {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <Card className="text-center shadow-sm" style={{ maxWidth: 500 }}>
          <Card.Body className="p-4">
            <h4 className="text-danger">{t('rejectedTitle', 'Receipt Verify Nahi Ho Saki')}</h4>
            <p className="text-muted">{rejectReason || t('rejectedDefaultReason', 'Receipt dobara check karein aur dobara upload karein.')}</p>
            <Button variant="primary" onClick={() => setStep('select')}>
              {t('btnTryAgain', 'Dobara Try Karein')}
            </Button>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (step === 'select') {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div style={{ maxWidth: 900, width: '100%' }}>
          <h4 className="text-center mb-4">{t('titleSelectPlan', 'Apna Plan Select Karein')}</h4>
          <Row className="g-3">
            {PLAN_DISPLAY.map((plan) => (
              <Col xs={12} md={4} key={plan.key}>
                <Card className="h-100 text-center shadow-sm">
                  <Card.Body>
                    <h5 className="fw-bold">{plan.label}</h5>
                    <div className="fs-4 fw-bold text-primary my-2">{plan.price.toLocaleString()} {t('currencyPkr', 'PKR')}</div>
                    <p className="text-muted small mb-1">{plan.sites}</p>
                    <p className="text-muted small mb-3">{plan.duration}</p>
                    <Button variant={plan.key === 'trial' ? 'outline-secondary' : 'primary'} className="w-100" onClick={() => handlePlanClick(plan.key)}>
                      {plan.key === 'trial' ? t('btnContinueFree', 'Continue Free') : t('btnSelectPlan', 'Select Plan')}
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </div>
    );
  }

  if (step === 'confirm' || step === 'uploading') {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <Card className="shadow-sm" style={{ maxWidth: 500, width: '100%' }}>
          <Card.Body className="p-4">
            <h5>{planInfo?.label} — {planInfo?.price.toLocaleString()} {t('currencyPkr', 'PKR')}</h5>

            {message && <Alert variant={message.type}>{message.text}</Alert>}

            <Alert variant="warning" className="mt-3">
              ⚠️ <strong>{t('warnNonRefundableHeading', 'Yeh payment refundable nahi hai.')}</strong> {t('warnNonRefundableText', 'Pehle tasdeeq karein ke aap sahi plan select kar chuke hain.')}
            </Alert>

            <Form.Check
              type="checkbox"
              id="refund-agree"
              label={t('chkNonRefundableAgree', 'Main samajh gaya/gayi hun ke yeh payment refund nahi hogi.')}
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mb-3"
            />

            {agreed && (
              <>
                <hr />
                <p className="mb-2">
                  <strong>1)</strong> {t('step1PaymentInstruction', 'Payment humare number pe bhejein aur WhatsApp par confirm karein:')}
                </p>

                <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(waMessage)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-success w-100 mb-3"
                  onClick={() => setWhatsappClicked(true)}
                >
                  📱 {t('btnSendWhatsApp', 'WhatsApp par Bhejein')} ({WHATSAPP_NUMBER})
                </a>

                <p className="mb-2"><strong>2)</strong> {t('step2UploadInstruction', 'Receipt screenshot yahan upload karein:')}</p>
                <Form onSubmit={handleUpload}>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={(e) => setFile(e.target.files[0])}
                    className="mb-3"
                  />
                  <Button
                    type="submit"
                    variant="primary"
                    className="w-100"
                    disabled={step === 'uploading' || !whatsappClicked}
                  >
                    {step === 'uploading'
                      ? <Spinner size="sm" />
                      : whatsappClicked
                        ? t('btnSubmitReceipt', 'Receipt Submit Karein')
                        : t('btnSendWhatsAppFirst', 'Pehle WhatsApp par bhejein ☝️')}
                  </Button>
                </Form>
              </>
            )}

            <Button variant="link" className="mt-2" onClick={() => setStep('select')}>
              {t('btnBackToPlans', '← Wapis Plans Dekhein')}
            </Button>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return null;
}